import { WIDGET_DEFAULT_FONT } from 'constants/fonts';
import { WritableDraft } from 'immer/dist/internal';
import clonedeep from 'lodash.clonedeep';
import mergeWith from 'lodash.mergewith';
import { AddGroupWidget, AddWidget, UpdateWidget } from 'modules/Editor/store/infographSlice.types';
import { PageId, WidgetId } from 'types/idTypes';
import { InfographState, PageSize, WidgetsMap } from 'types/infographTypes';
import { Subset } from 'types/object.types';
import { Widget, WidgetType } from 'types/widget.types';
import { shuffleColors } from 'utils/colorShuffle';
import { findAllPropertyValues } from 'utils/findAllPropertyValues';
import { ImageWidgetData } from 'widgets/ImageWidget/ImageWidget.types';
import { LineWidgetData } from 'widgets/LineWidget/LineWidget.types';
import { ResponsiveWidgetBaseData } from 'widgets/ResponsiveWidgets/ResponsiveWidgetBase.types';
import { rescaleCells } from 'widgets/TextBasedWidgets/TableWidget/TableWidget.helpers';
import { TableWidgetData } from 'widgets/TextBasedWidgets/TableWidget/TableWidget.types';
import { TextWidgetData } from 'widgets/TextBasedWidgets/TextWidget/TextWidget.types';
import { modifyAllFontSizes } from 'widgets/TextBasedWidgets/common/TextBasedWidgets.helpers';
import { TextBasedWidgetData } from 'widgets/TextBasedWidgets/common/TextBasedWidgets.types';
import { generateWidgetId, getPageWidgetIds, getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { AllWidgetData } from 'widgets/Widget.types';
import { GroupWidgetData, WidgetFontProperty, WidgetMap } from 'widgets/Widget.types';
import { lineConnectionCache } from 'widgets/sdk/LineConnectionCache';
import { PAGE_NUMBER_FONT_FAMILY } from 'widgets/sdk/PageNumber/PageNumber.config';

/**
 *
 * @param node
 * @param widgetId
 * @param positionedNextTo If present, the new Widget ID will be added next to this Widget ID in the array, instead of last.
 */
export function addToLeafTree(
  node: [string, {}, Array<any> | string],
  widgetId: WidgetId,
  positionedNextTo: WidgetId = '',
) {
  const [, , l] = node;
  if (typeof l !== 'string') {
    const targetIndex = l.findIndex((elem) => elem[2] === positionedNextTo);

    const insertIndex = targetIndex === -1 ? l.length : targetIndex + 1; // If not found, push last.
    l.splice(insertIndex, 0, ['div', {}, widgetId]);
  }
}

/**
 * Removes a node from a tree structure based on the provided widgetId.
 * If the node is a leaf node, it will be removed from the tree.
 * If the node is not a leaf node, the removal operation will be performed recursively on all child nodes.
 *
 * @param node - The node to remove from the tree.
 * @param widgetId - The widgetId of the node to remove.
 *
 */
export function removeFromTree(node: [string, {}, Array<any> | string], widgetId: WidgetId) {
  const [, , l] = node;
  let removeIndex = -1;

  // do nothing if leaf is not an array
  if (!l) return;

  // this node is already leaf, cannot remove this case... ? should this return empty array?
  if (typeof l === 'string') return;

  for (let i = 0; i < l.length; ++i) {
    const [, , ll] = l[i];
    if (ll === widgetId) {
      removeIndex = i;
      break;
    }
  }

  if (removeIndex !== -1) {
    l.splice(removeIndex, 1);
    return;
  }

  // not found, so recursively remove from all child
  for (const ll of l) {
    removeFromTree(ll, widgetId);
  }
}

// Mutates the state and removes the widget from all places
export const removeWidgetById = (widgetId: WidgetId, state: InfographState, pageId: PageId) => {
  const { widgetLayerOrder, widgetStructureTree } = state.pages[pageId];
  const widget = state.widgets[widgetId];

  // Go through each connected line, delete them.
  const connectedLineIds = lineConnectionCache.getAttachedLines(widgetId);
  if (!!connectedLineIds) {
    connectedLineIds.forEach((lineWidgetId: WidgetId) => {
      removeWidgetById(lineWidgetId, state, pageId);
    });
  }

  // Update connected line widgets if any
  removeWidgetLineConnections(widgetId, state);

  if (widget) {
    // Remove from widget map
    delete state.widgets[widgetId];
  } else {
    console.warn(`No delete, Widget ${widgetId} not found in widgets map!`);
  }

  // Remove from layer order
  if (widgetLayerOrder) {
    const widgetIndex = widgetLayerOrder.findIndex((w) => w === widgetId);
    if (widgetIndex !== -1) widgetLayerOrder.splice(widgetIndex, 1);
  }

  // Remove from the content structure
  if (widgetStructureTree) {
    removeFromTree(widgetStructureTree, widgetId);
  }
};

/**
 * Updates the line widget data in the state when a widget is removed and cleans up line connection cache
 * If the widget specified by `widgetId` has connected lines, will update the line widget start/end port
 *
 * Mutates state
 *
 * @param widgetId - Widget being removed
 * @param state
 */
export const removeWidgetLineConnections = (widgetId: WidgetId, state: InfographState) => {
  // Update cache if deleting a line widget
  if (getWidgetTypeFromId(widgetId) === WidgetType.Line) {
    const widget = state.widgets[widgetId] as LineWidgetData;
    if (!widget) return;

    if (widget.startPort?.widgetId) {
      lineConnectionCache.detachLineFromWidget(widget.startPort.widgetId, widgetId);
    }
    if (widget.endPort?.widgetId) {
      lineConnectionCache.detachLineFromWidget(widget.endPort.widgetId, widgetId);
    }

    return;
  }

  const connectedLineIds = lineConnectionCache.getAttachedLines(widgetId);

  // Widget doesn't have connection
  if (!connectedLineIds) return;

  connectedLineIds.forEach((lineWidgetId: WidgetId) => {
    const widget = state.widgets[lineWidgetId] as LineWidgetData;
    if (!widget) return;

    if (widget.startPort?.widgetId === widgetId) {
      widget.startPort = null;
    } else if (widget.endPort?.widgetId === widgetId) {
      widget.endPort = null;
    }
  });

  lineConnectionCache.remove(widgetId);
};

/**
 * Removes a widget and all its nested members (if any) from the state
 *
 * @param widgetId widget to remove
 * @param state
 * @param pageId
 */
export const removeWidgetByIdNested = (widgetId: WidgetId, state: InfographState, pageId: PageId) => {
  const widget = state.widgets[widgetId];

  if (widget?.hasOwnProperty('memberWidgetIds')) {
    const { memberWidgetIds } = widget as GroupWidgetData;
    memberWidgetIds.forEach((mwId: WidgetId) => removeWidgetByIdNested(mwId, state, pageId));
  }

  removeWidgetById(widgetId, state, pageId);
};

/**
 * @param state
 * @param widget New widget data
 * @param positionedNextTo If present, the new widget will be positioned next to this widget ID in Reading Order and Layer Order
 * @param isGroupMember If the widget is group member
 */
export const addWidgetToState = (
  state: InfographState,
  widget: AddWidget,
  positionedNextTo: WidgetId = '',
  isGroupMember?: boolean,
) => {
  const { pageId, widgetId, widgetData, groupWidgets, isResponsiveGroup } = widget;
  const { widgetLayerOrder, widgetStructureTree } = state.pages[pageId];
  const isGroup = groupWidgets && groupWidgets.length;

  state.widgets[widgetId] = widgetData;

  // Add to layer - group members are not added to the layer order
  if (widgetLayerOrder && !isGroupMember) {
    const targetIndex = widgetLayerOrder.indexOf(positionedNextTo);
    const insertIndex = targetIndex === -1 ? widgetLayerOrder.length : targetIndex + 1; // If not found, push last.
    widgetLayerOrder.splice(insertIndex, 0, widgetId);
  }

  if (positionedNextTo && widgetStructureTree) {
    addToLeafTree(widgetStructureTree, widgetId, positionedNextTo);
  }

  // Add to tree structure
  if (!positionedNextTo && widgetStructureTree && (!isGroup || isResponsiveGroup)) {
    addToLeafTree(widgetStructureTree, widgetId);
  }

  if (isGroup) {
    groupWidgets.forEach((groupWidget: AddGroupWidget) => {
      const { widgetId, widgetData, groupWidgets } = groupWidget;
      // group widgets are not added to the widgetLayerOrder
      state.widgets[widgetId] = widgetData;

      // Add nested members to state.widgets
      if (groupWidgets) {
        groupWidgets.forEach((nestedGroupWidget: AddGroupWidget) => {
          const { widgetId: nestedWidgetId, widgetData: nestedWidgetData } = nestedGroupWidget;
          state.widgets[nestedWidgetId] = nestedWidgetData;
        });
      }

      // Add to tree structure
      if (widgetStructureTree && !isResponsiveGroup) addToLeafTree(widgetStructureTree, widgetId);
    });
  }
};

export function replaceInTree(node: [string, {}, Array<any> | string], widgetId: WidgetId, replaceId: WidgetId) {
  if (!node) {
    return;
  }

  const [, , l] = node;

  if (!l) return;
  if (typeof l === 'string') return;

  for (let i = 0; i < l.length; ++i) {
    const [, , ll] = l[i];

    if (ll === widgetId) {
      l[i][2] = replaceId;
      break;
    }
  }

  for (const ll of l) {
    replaceInTree(ll, widgetId, replaceId);
  }
}

/**
 * Find all unique font familes from giving infograph page
 *
 * @param infograph A infograph state
 * @param pageId - A page id
 * @param includeDefaultWidgetFont - Include default widget font in the list (e.g. when loading a page where you can add widgets)
 * @returns  A list with unique font family names e.g. ['Inter', 'Roboto']
 */
export const findFontsInPage = (
  infograph: InfographState,
  pageId: string,
  includeDefaultWidgetFont = false,
): string[] => {
  if (!pageId || !infograph?.pages?.[pageId]) return [];

  const allPageWidgetIds = getPageWidgetIds(infograph?.pages?.[pageId], infograph.widgets);
  const allPageWidgets = allPageWidgetIds.map((id) => infograph.widgets[id]);

  const widgetFonts = findFontsInObjectOrArray(allPageWidgets);
  const pageNumberFonts = [];
  if (infograph?.pageNumber && infograph.pageNumber?.isEnabled) pageNumberFonts.push(PAGE_NUMBER_FONT_FAMILY);

  return [...new Set([...widgetFonts, ...pageNumberFonts, ...(includeDefaultWidgetFont ? [WIDGET_DEFAULT_FONT] : [])])];
};

/**
 * Find all font families in an object/array
 *
 * @param obj
 * @returns list of font families
 */
export const findFontsInObjectOrArray = (obj: any): string[] => {
  if (!obj) return [];
  return findAllPropertyValues(obj, Object.values(WidgetFontProperty)) as string[];
};

/**
 * Mutates the state and duplicates the member widgets of a group
 * Handles case when member widget is a parent (i.e. responsive widget)
 *
 * @param infograph state.infograph - will be updated
 * @param duplicatePageWidgets - page widget map
 * @param newWidgetStructureTree - widget structure tree will be updated
 *
 * @param memberWidgetIds - members to duplicate
 * @param parentWidget
 * @param parentWidgetId
 * @param isResponsiveGroup
 */
export const duplicateNestedMemberWidgets = (
  infograph: InfographState,
  duplicatePageWidgets: WidgetMap,
  newWidgetStructureTree: [string, {}, Array<any> | string],
  memberWidgetIds: WidgetId[],
  parentWidget: GroupWidgetData | ResponsiveWidgetBaseData,
  parentWidgetId: WidgetId,
  isResponsiveGroup: boolean,
) => {
  memberWidgetIds.forEach((mwId: WidgetId) => {
    const newWidgetType = getWidgetTypeFromId(mwId);
    const newWidgetId = generateWidgetId(newWidgetType);
    let newWidget = clonedeep(duplicatePageWidgets[mwId]);

    replaceInTree(newWidgetStructureTree, mwId, newWidgetId);

    // if nested, duplicate widget members, update memberWidgetIds, update componentWidgetIdMap
    let newNestedWidget = newWidget;
    if (newWidget.hasOwnProperty('memberWidgetIds')) {
      newNestedWidget = {
        ...newWidget,
        memberWidgetIds: [],
        componentWidgetIdMap: {},
      } as ResponsiveWidgetBaseData;

      duplicateNestedMemberWidgets(
        infograph,
        duplicatePageWidgets,
        newWidgetStructureTree,
        (newWidget as GroupWidgetData).memberWidgetIds,
        newNestedWidget as ResponsiveWidgetBaseData,
        mwId,
        true,
      );
      newWidget = newNestedWidget;
    }

    infograph.widgets[newWidgetId] = newWidget;
    parentWidget.memberWidgetIds.push(newWidgetId);

    if (isResponsiveGroup) {
      const { componentWidgetIdMap } = duplicatePageWidgets[parentWidgetId] as ResponsiveWidgetBaseData;
      const componentKey = Object.keys(componentWidgetIdMap).find((key) => componentWidgetIdMap[key] === mwId);
      if (componentKey) {
        (parentWidget as ResponsiveWidgetBaseData).componentWidgetIdMap[componentKey] = newWidgetId;
      }
    }
  });
};

/**
 * Update a widget with new properties.
 * The update is recursive and uses lodash.mergeWith
 *
 * @param widget widget that should be updated
 * @param newWidgetData - subset of widget data that should be updated
 *
 * e.g.
 * widget = { border: { color: 'red', weight: 10,  } }
 * newWidgetData = { border: { color: 'blue' } }
 *
 * result -> { border: { color: 'blue', weight: 10 } }
 *
 */

export const mergeWidgetData = (widget: Widget, newWidgetData: Subset<AllWidgetData>) =>
  // recursive merge properties for nested widget data
  // overwrite array properties
  mergeWith(widget, newWidgetData, (a, b) => (Array.isArray(b) ? b : undefined));

/**
 * Returns a copy of `infograph` with `colorPalette` applied to the pages specified
 * by `pageIds` and the widgets on the pages.
 *
 * @param infograph Infograph to apply color palette to
 * @param pageIds Page ids to update
 * @param colorPalette Color palette to use for update
 * @returns updated infograph state
 */
export const applyColorPalette = (infograph: InfographState, pageIds: PageId[], colorPalette: string[]) => {
  const updatedInfograph = clonedeep(infograph);
  const { widgets: widgetMap } = updatedInfograph;

  for (const pageId of pageIds) {
    const page = updatedInfograph.pages[pageId];
    const pageBackground = page.background;
    const gradientBackground = page.gradientBackground;

    // TODO should add support for gradients in colour shuffle
    // For now, just use the first colour in the gradient - gradient will be lost after palette is applied
    let pageColor = '';
    if (gradientBackground) {
      pageColor = gradientBackground.colors[0];
    } else {
      pageColor = pageBackground;
    }

    const pageWidgetMap = getPageWidgetIds(page, widgetMap).reduce((acc, widgetId) => {
      acc[widgetId] = widgetMap[widgetId];
      return acc;
    }, {} as WidgetMap);

    // Shuffle colors
    const { widgetUpdates, newPageColor } = shuffleColors(pageWidgetMap, pageColor, colorPalette);

    // Update page colour - only solid colour supported so reset gradient
    updatedInfograph.pages[pageId].background = newPageColor;
    updatedInfograph.pages[pageId].gradientBackground = null;

    // Update widgets
    const widgets = updatedInfograph.widgets;
    for (const widgetUpdate of widgetUpdates) {
      const { widgetId, widgetData: newWidgetData } = widgetUpdate;
      const widget = widgets[widgetId];
      widgets[widgetId] = mergeWidgetData(widget, newWidgetData);
    }
  }

  return updatedInfograph;
};

/**
 * Returns a copy of `infograph` with `colorPalette` applied to all the infograph pages
 * by a list of `pageIds` and the widgets on the pages.
 *
 * @param infograph Infograph to apply color palette to
 * @param pageIds[] All the Page ids to update
 * @param colorPalette Color palette to use for update
 * @returns updated infograph state
 */
export const applyAllPagesColorPalette = (infograph: InfographState, pageIds: PageId[], colorPalette: string[]) => {
  const updatedInfograph = clonedeep(infograph);
  const { widgets: widgetMap } = updatedInfograph;

  // Get the first page's color for shuffle
  // this is to ensure that the background color scheme for all pages remain consistent
  // As for widgets will shuffle based on widgetMap
  const firstPage = updatedInfograph.pages[pageIds[0]];
  const firstPageColor = firstPage.gradientBackground ? firstPage.gradientBackground.colors[0] : firstPage.background;

  const { widgetUpdates, newPageColor } = shuffleColors(widgetMap, firstPageColor, colorPalette);

  for (const pageId of pageIds) {
    updatedInfograph.pages[pageId].background = newPageColor;
    updatedInfograph.pages[pageId].gradientBackground = null;

    // Update widgets
    const widgets = updatedInfograph.widgets;

    for (const widgetUpdate of widgetUpdates) {
      const { widgetId, widgetData: newWidgetData } = widgetUpdate;
      const widget = widgets[widgetId];
      widgets[widgetId] = mergeWidgetData(widget, newWidgetData);
    }
  }

  return updatedInfograph;
};

/**
 * Store changed widgets into infograph state, this function will mutate both params.
 *
 * @param state {InfographState}
 * @param widgetsToUpdate list of widgets to update
 */
export const mutateWidgets = (state: WritableDraft<InfographState>, widgetsToUpdate: UpdateWidget[]) => {
  widgetsToUpdate.forEach((updateWidget: UpdateWidget) => {
    const { widgetId, widgetData } = updateWidget;
    const widget = state.widgets[widgetId];

    mergeWidgetData(widget, widgetData);
  });
};

/**
 * Scale widget position and size.
 *
 * @param widgets Widgets to be scaled
 * @param param1 xRatio, yRatio
 * @param param2 paddingTop, paddingLeft
 * @returns {WidgetsMap}
 */
export const scaleWidgets = (
  widgets: WidgetMap,
  [xRatio, yRatio]: [number, number],
  [marginTopPx, marginLeftPx]: [number, number] = [0, 0],
): UpdateWidget[] | undefined => {
  // TODO: Should be reusable with `templateResize` function.
  let left: number;
  let top: number;
  let width: number | undefined;
  let height: number | undefined;
  let batchWidgetUpdate: UpdateWidget[] = [];
  let widgetType: WidgetType;
  const xRatioInv = 1 / xRatio;
  const yRatioInv = 1 / yRatio;

  Object.entries(widgets).forEach(([id, currentWidget]) => {
    widgetType = getWidgetTypeFromId(id);
    left = currentWidget.leftPx * xRatioInv + marginLeftPx;
    top = currentWidget.topPx * yRatioInv + marginTopPx;
    width = currentWidget.widthPx * xRatioInv;
    height = currentWidget.heightPx && currentWidget.heightPx * yRatioInv;

    switch (widgetType) {
      case WidgetType.Text:
        const textWidgetData = currentWidget as TextBasedWidgetData;
        if (textWidgetData.proseMirrorData) {
          const newContent = modifyAllFontSizes(textWidgetData.proseMirrorData, xRatioInv, { round: true });
          batchWidgetUpdate.push({
            widgetId: id,
            widgetData: {
              ...textWidgetData,
              leftPx: left,
              topPx: top,
              widthPx: width,
              proseMirrorData: newContent,
            } as TextWidgetData,
          });
        }
        break;
      case WidgetType.Table:
        const tableWidgetData = currentWidget as TextBasedWidgetData;
        if (tableWidgetData.proseMirrorData) {
          const newContent = modifyAllFontSizes(rescaleCells(tableWidgetData.proseMirrorData, xRatioInv), xRatioInv, {
            round: true,
          });
          batchWidgetUpdate.push({
            widgetId: id,
            widgetData: {
              ...tableWidgetData,
              leftPx: left,
              topPx: top,
              widthPx: width,
              proseMirrorData: newContent,
            } as TableWidgetData,
          });
        }
        break;
      case WidgetType.Image:
        const imageWidgetData = currentWidget as ImageWidgetData;
        batchWidgetUpdate.push({
          widgetId: id,
          widgetData: {
            ...imageWidgetData,
            leftPx: left,
            topPx: top,
            widthPx: width,
            heightPx: height,
            imageRect: {
              widthPx: imageWidgetData.imageRect.widthPx * xRatioInv,
              heightPx: imageWidgetData.imageRect.heightPx * xRatioInv,
            },
            crop: {
              top: imageWidgetData.crop.top * xRatioInv,
              bottom: imageWidgetData.crop.bottom * xRatioInv,
              left: imageWidgetData.crop.left * xRatioInv,
              right: imageWidgetData.crop.right * xRatioInv,
            },
          } as ImageWidgetData,
        });
        break;
      case WidgetType.Line:
        const lineWidgetData = currentWidget as LineWidgetData;
        batchWidgetUpdate.push({
          widgetId: id,
          widgetData: {
            ...lineWidgetData,
            leftPx: left,
            topPx: top,
            widthPx: width,
            heightPx: height,
            startPos: {
              xPx: lineWidgetData.startPos.xPx * xRatioInv,
              yPx: lineWidgetData.startPos.yPx * yRatioInv,
            },
            endPos: {
              xPx: lineWidgetData.endPos.xPx * xRatioInv,
              yPx: lineWidgetData.endPos.yPx * yRatioInv,
            },
          },
        });
        break;
      default:
        batchWidgetUpdate.push({
          widgetId: id,
          widgetData: {
            ...currentWidget,
            leftPx: left,
            topPx: top,
            widthPx: width,
            heightPx: height,
          },
        });
        break;
    }
  });

  return batchWidgetUpdate;
};

/**
 * Scale widget position and size, then returns the WidgetsMap object.
 *
 * @param widgets Widgets to be scaled
 * @param param1 xRatio, yRatio
 * @param param2 paddingTop, paddingLeft
 * @returns {WidgetsMap}
 */
export const scaleAndMapWidgets = (
  widgets: WidgetMap,
  [xRatio, yRatio]: [number, number],
  [marginTopPx, marginLeftPx]: [number, number] = [0, 0],
) => {
  const scaledWidgets = scaleWidgets(widgets, [xRatio, yRatio], [marginTopPx, marginLeftPx]);
  const mappedWidgets = scaledWidgets?.reduce((widgetsMap: Record<string, Subset<AllWidgetData>>, item) => {
    widgetsMap[item.widgetId] = item.widgetData;
    return widgetsMap;
  }, {});

  return mappedWidgets as WidgetsMap;
};

/**
 * Calculate ratio and needed padding.
 *
 * @param currentSize {PageSize}
 * @param previousSize {PageSize}
 * @returns { xRatio, yRatio, paddingLeftPx, paddingRightPx }
 */
export const calculateContent = (currentSize: Required<PageSize>, previousSize: Required<PageSize>) => {
  // Previous actual paper size, or currently in redux state.
  const previousAspectRatio: number = previousSize.widthPx / previousSize.heightPx;

  // TODO: Don't rescale if current size is smaller but content still in paper

  // Content size to calculate all widgets width and height in page.
  const contentSize = { ...previousSize, ...currentSize } as Required<PageSize>;

  if (currentSize.widthPx > currentSize.heightPx) {
    // If landscape
    // Calculate width based on current height, since current height is less than its width, yeah it is landscape.
    // Apply to portrait as well.
    contentSize.widthPx = contentSize.heightPx * previousAspectRatio;

    if (contentSize.widthPx > currentSize.widthPx) {
      contentSize.widthPx = currentSize.widthPx;
      contentSize.heightPx = contentSize.widthPx / previousAspectRatio;
    }
  } else {
    // Portrait
    contentSize.heightPx = contentSize.widthPx / previousAspectRatio;

    // correct if getting out of boundary
    if (contentSize.heightPx > currentSize.heightPx) {
      contentSize.heightPx = currentSize.heightPx;
      contentSize.widthPx = contentSize.heightPx * previousAspectRatio;
    }
  }

  // To resize and reposition all widgets
  const [xRatio, yRatio] = [previousSize.widthPx / contentSize.widthPx, previousSize.heightPx / contentSize.heightPx];

  // Top margin or horizontal position adjustment, can be positive or negative numeric values
  const paddingTopPx = (currentSize.heightPx - contentSize.heightPx) / 2;

  // Left margin or vertical position adjustment, can be positive or negative numeric values
  const paddingLeftPx = (currentSize.widthPx - contentSize.widthPx) / 2;

  // TODO: Padding need to be reset each time rescale applied, to prevent margin also calculated when rescaled

  return { xRatio, yRatio, paddingTopPx, paddingLeftPx };
};
